import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllISOCodes, Currency } from 'iso-country-currency';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { RxUtils, Tr } from '@utils';
import {
	AccessoryProperties,
	AccessoryType,
	ChargerCurrentType,
	ChargerPhaseType,
	ChargerProperties,
	ChargerUsingType,
	Product,
	ProductCategoryType,
	ProductPriceRecurrence,
	ProductPriceType,
	productService,
	ServiceProperties,
	ServiceType,
} from '@services';
import { Autocomplete, Box, Grid, MenuItem, Stack } from '@mui/material';
import { FormikCheckbox, FormikNumber, FormikRadio, FormikRadioGroup, FormikTextfield, ImageUpload } from '@components/common';
import { FormStepGreySection, FormStepLayout, FormStepQuestion } from '@components/portal/common';

const DEFAULT_COUNTRY_ISO = 'IT';
const COUNTRIES = getAllISOCodes();
const CURRENCIES = [...new Set(COUNTRIES.map(({ currency }) => currency))]
	.sort((a, b) => a.localeCompare(b))
	.map((currency) => COUNTRIES.find((country) => country.currency === currency))
	.filter(Boolean) as Currency[];

type FormFields = Extendable<{
	name: string;
	description?: string;
	installationCost?: number;
	category?: ProductCategoryType;
	country?: string;
	currency?: string;
	price?: number;
	priceType?: ProductPriceType;
	priceRecurrence?: ProductPriceRecurrence;

	// ChargerProperties
	brand: string;
	numberOfConnectors: number;
	currentType: ChargerCurrentType;
	maximumPower: number;
	ratedVoltage: number;
	maxCurrent: number;
	chargerUsingType: ChargerUsingType;
	cableIncluded: boolean;
	poleNeeded: boolean;
	requiredAccessoryTypes: AccessoryType[];
	homeUsage: boolean;
	officeUsage: boolean;

	// AccessoryProperties
	numberOfAccesoryPerChargingPoint: number;
	accessoryType: AccessoryType;

	// ServiceProperties
	serviceType: ServiceType;

	// both ChargerProperties & AccessoryProperties
	chargerPhaseType: ChargerPhaseType;
}>;

const getAccessoryTypeText = (inp: AccessoryType): ReactNode => {
	if (inp === AccessoryType.CABLE) {
		return <Tr.Portal path="product.accessory-type-cable" />;
	} else if (inp === AccessoryType.DPM_MONO_PHASE) {
		return <Tr.Portal path="product.accessory-type-dpm-mono-phase" />;
	} else if (inp === AccessoryType.DPM_THREE_PHASE) {
		return <Tr.Portal path="product.accessory-type-dpm-three-phase" />;
	} else if (inp === AccessoryType.MID_MONO_PHASE) {
		return <Tr.Portal path="product.accessory-type-mid-mono-phase" />;
	} else if (inp === AccessoryType.MID_THREE_PHASE) {
		return <Tr.Portal path="product.accessory-type-mid-three-phase" />;
	} else if (inp === AccessoryType.POLE) {
		return <Tr.Portal path="product.accessory-type-pole" />;
	} else if (inp === AccessoryType.RFID) {
		return <Tr.Portal path="product.accessory-type-rfid" />;
	} else {
		return inp;
	}
};
const getChargerPhaseTypeText = (inp: ChargerPhaseType): ReactNode => {
	if (inp === ChargerPhaseType.MONOPHASE) {
		return <Tr.Portal path="product.charger-phase-type-mono-phase" />;
	} else if (inp === ChargerPhaseType.TRIPHASE) {
		return <Tr.Portal path="product.charger-phase-type-three-phase" />;
	} else {
		return inp;
	}
};
const getChargerUsingTypeText = (inp: ChargerUsingType): ReactNode => {
	if (inp === ChargerUsingType.PRIVATE) {
		return <Tr.Portal path="product.charger-using-type-private" />;
	} else if (inp === ChargerUsingType.PUBLIC) {
		return <Tr.Portal path="product.charger-using-type-public" />;
	} else {
		return inp;
	}
};
const getProductCategoryTypeText = (inp: ProductCategoryType): ReactNode => {
	if (inp === ProductCategoryType.ACCESSORIES) {
		return <Tr.Portal path="product.product-category-type-accessories" />;
	} else if (inp === ProductCategoryType.CHARGING_POINT) {
		return <Tr.Portal path="product.product-category-type-charging-point" />;
	} else if (inp === ProductCategoryType.SERVICE) {
		return <Tr.Portal path="product.product-category-type-service" />;
	} else {
		return inp;
	}
};
const getProductPriceRecurrenceText = (inp: ProductPriceRecurrence): ReactNode => {
	if (inp === ProductPriceRecurrence.ONE_TIME) {
		return <Tr.Portal path="product.price-recurrence-one-time" />;
	} else if (inp === ProductPriceRecurrence.YEAR_SUBSCRIPTION) {
		return <Tr.Portal path="product.price-recurrence-year-subscription" />;
	} else {
		return inp;
	}
};
const getProductPriceTypeText = (inp: ProductPriceType): ReactNode => {
	if (inp === ProductPriceType.PER_CHARGING_POINT) {
		return <Tr.Portal path="product.price-type-per-charging-point" />;
	} else if (inp === ProductPriceType.PER_UNIT) {
		return <Tr.Portal path="product.price-type-per-unit" />;
	} else {
		return inp;
	}
};
const getServiceTypeText = (inp: ServiceType): ReactNode => {
	if (inp === ServiceType.CONNECTIVITY) {
		return <Tr.Portal path="product.service-type-connectivity" />;
	} else if (inp === ServiceType.MAINTENANCE) {
		return <Tr.Portal path="product.service-type-maintenance" />;
	} else if (inp === ServiceType.SOFTWARE) {
		return <Tr.Portal path="product.service-type-software" />;
	} else {
		return inp;
	}
};

export interface IAddEditProductFormProps {
	values?: Partial<Product>;
	onSubmit?: (values: Partial<Product>, images?: File[]) => void;
}

export const AddEditProductForm: FC<IAddEditProductFormProps> = ({ values, onSubmit }) => {
	const { t } = useTranslation('portal');
	const [imagesChanged, setImagesChanged] = useState(false);
	const [currentImages, setCurrentImages] = useState<string[]>([]);
	const [newImages, setNewImages] = useState<File[]>([]);
	const { properties, ...initialValues } = values || {};

	useEffect(() => {
		values?.id && RxUtils.promisify(productService.getImages(values as Product), setCurrentImages);
	}, [values]);

	const formik = useFormik<FormFields>({
		initialValues: {
			name: '',
			description: '',
			installationCost: '',
			category: '',
			price: '',
			priceType: '',
			priceRecurrence: '',

			// ChargerProperties
			brand: '',
			numberOfConnectors: '',
			currentType: '',
			maximumPower: '',
			ratedVoltage: '',
			maxCurrent: '',
			chargerUsingType: '',
			requiredAccessoryTypes: [],

			// AccessoryProperties
			numberOfAccesoryPerChargingPoint: '',
			accessoryType: '',

			// ServiceProperties
			serviceType: '',

			// both ChargerProperties & AccessoryProperties
			chargerPhaseType: '',

			...initialValues,
			...properties,
			country: initialValues.country || DEFAULT_COUNTRY_ISO,
			currency: initialValues.currency || COUNTRIES.find(({ iso }) => iso === DEFAULT_COUNTRY_ISO)?.currency || '',
			cableIncluded: (properties as ChargerProperties)?.cableIncluded ? '1' : '0',
			poleNeeded: (properties as ChargerProperties)?.poleNeeded ? '1' : '0',
			homeUsage: (properties as ChargerProperties)?.homeUsage ? '1' : '0',
			officeUsage: (properties as ChargerProperties)?.officeUsage ? '1' : '0',
		},
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			name: yup.string().required(t('field-required')),
			description: yup.string().optional(),
			installationCost: yup.string().required(t('field-required')),
			category: yup.string().required(t('field-required')),
			country: yup.string().required(t('field-required')),
			currency: yup.string().required(t('field-required')),
			price: yup.number().required(t('field-required')),
			priceType: yup.string().required(t('field-required')),
			priceRecurrence: yup.string().required(t('field-required')),

			// both ChargerProperties & AccessoryProperties
			chargerPhaseType: yup.string().when('category', {
				is: (value: ProductCategoryType) => [ProductCategoryType.ACCESSORIES, ProductCategoryType.CHARGING_POINT].includes(value),
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),

			// ChargerProperties
			brand: yup.string().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),
			numberOfConnectors: yup.number().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.number().required(t('field-required')),
				otherwise: yup.number().nullable(),
			}),
			currentType: yup.string().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),
			maximumPower: yup.number().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.number().required(t('field-required')),
				otherwise: yup.number().nullable(),
			}),
			ratedVoltage: yup.number().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.number().required(t('field-required')),
				otherwise: yup.number().nullable(),
			}),
			maxCurrent: yup.number().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.number().required(t('field-required')),
				otherwise: yup.number().nullable(),
			}),
			chargerUsingType: yup.string().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),
			cableIncluded: yup.string().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),
			poleNeeded: yup.string().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),
			homeUsage: yup.string().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),
			officeUsage: yup.string().when('category', {
				is: ProductCategoryType.CHARGING_POINT,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),
			requiredAccessoryTypes: yup
				.array()
				.of(yup.string())
				.when('category', {
					is: ProductCategoryType.CHARGING_POINT,
					then: yup.array().of(yup.string()).required(t('field-required')),
					otherwise: yup.array().of(yup.string()).nullable(),
				}),

			// ChargerProperties
			numberOfAccesoryPerChargingPoint: yup.number().when('category', {
				is: ProductCategoryType.ACCESSORIES,
				then: yup.number().required(t('field-required')),
				otherwise: yup.number().nullable(),
			}),
			accessoryType: yup.string().when('category', {
				is: ProductCategoryType.ACCESSORIES,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),

			// ServiceProperties
			serviceType: yup.string().when('category', {
				is: ProductCategoryType.SERVICE,
				then: yup.string().required(t('field-required')),
				otherwise: yup.string().nullable(),
			}),
		}),
		onSubmit: (
			{
				name,
				description,
				installationCost,
				category,
				country,
				currency,
				price,
				priceType,
				priceRecurrence,
				chargerPhaseType, // both ChargerProperties & AccessoryProperties

				// ChargerProperties
				brand,
				numberOfConnectors,
				currentType,
				maximumPower,
				ratedVoltage,
				maxCurrent,
				chargerUsingType,
				cableIncluded,
				poleNeeded,
				homeUsage,
				officeUsage,
				requiredAccessoryTypes,

				// ChargerProperties
				numberOfAccesoryPerChargingPoint,
				accessoryType,

				// ServiceProperties
				serviceType,
			},
			api
		) => {
			const product: Partial<Product> = {
				name,
				description,
				installationCost: +installationCost,
				category,
				country,
				currency,
				price: +price,
				priceType,
				priceRecurrence,
			};

			if (category === ProductCategoryType.CHARGING_POINT) {
				product.properties = {
					brand,
					chargerPhaseType,
					numberOfConnectors: +numberOfConnectors,
					currentType,
					maximumPower: +maximumPower,
					ratedVoltage: +ratedVoltage,
					maxCurrent: +maxCurrent,
					chargerUsingType,
					cableIncluded: cableIncluded === '1',
					poleNeeded: poleNeeded === '1',
					homeUsage: homeUsage === '1',
					officeUsage: officeUsage === '1',
					requiredAccessoryTypes: requiredAccessoryTypes || [],
				} as ChargerProperties;
			} else if (category === ProductCategoryType.ACCESSORIES) {
				product.properties = {
					chargerPhaseType,
					numberOfAccesoryPerChargingPoint: +numberOfAccesoryPerChargingPoint,
					accessoryType,
				} as AccessoryProperties;
			} else if (category === ProductCategoryType.SERVICE) {
				product.properties = { serviceType } as ServiceProperties;
			}
			onSubmit?.(product, newImages);
			api.setSubmitting(false);
		},
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = imagesChanged ? false : !formik.isValid || formik.isSubmitting;

	return (
		<form onSubmit={handleSubmit} style={{ flexGrow: 1, overflowY: 'hidden' }}>
			<FormStepLayout
				primaryButtonProps={{
					disabled,
					children: <Tr.Portal path={values ? 'product.update-entity' : 'product.add-entity'} />,
					onClick: submitForm,
					'data-testid': 'add-edit-product-submit-button',
				}}
				hideSecondaryButton
			>
				<Stack direction="column" spacing={8}>
					<FormStepQuestion title={<Tr.Portal path="product.add-edit-entity.product-details" />} titleProps={{ variant: 'h4' }}>
						<Grid container rowSpacing={5} columnSpacing={4}>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="name"
									title={t('product.name')}
									placeholder={t('product.name-placeholder')}
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="description"
									title={t('product.description')}
									placeholder={t('product.description-placeholder')}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									select
									formikApi={formik}
									name="category"
									title={t('product.category')}
									placeholder={t('product.category-placeholder')}
									required
								>
									{Object.values(ProductCategoryType).map((type) => (
										<MenuItem key={`product-category-type-${type}`} value={type}>
											{getProductCategoryTypeText(type)}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="installationCost"
									title={t('product.installation-cost')}
									placeholder={t('product.installation-cost-placeholder')}
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<Autocomplete
									isOptionEqualToValue={(option, value) => option.iso === value.iso}
									getOptionLabel={(option) => option?.countryName || ''}
									options={COUNTRIES}
									fullWidth
									autoHighlight
									onChange={(e, value) => {
										formik.setFieldValue('country', value?.iso || '');
										formik.setFieldValue('currency', value?.currency || '');
									}}
									onInputChange={(_e, _value, reason) => {
										if (reason === 'clear') {
											formik.setFieldValue('country', '');
											formik.setFieldValue('currency', '');
										}
									}}
									inputValue={
										COUNTRIES.find(({ iso }) => iso === formik.values.country)?.countryName || formik.values.country
									}
									renderOption={(props, option) => (
										<Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
											<img
												loading="lazy"
												width="20"
												src={`https://flagcdn.com/w20/${option.iso.toLowerCase()}.png`}
												srcSet={`https://flagcdn.com/w40/${option.iso.toLowerCase()}.png 2x`}
												alt=""
											/>
											{option.countryName}
										</Box>
									)}
									renderInput={(params) => (
										<FormikTextfield
											{...params}
											formikApi={formik}
											name="country"
											title={t('product.country')}
											placeholder={t('product.country-placeholder')}
											required
											InputProps={{
												...params.InputProps,
												endAdornment: params.InputProps.endAdornment,
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={4}>
								<Autocomplete
									isOptionEqualToValue={(option, value) => option.currency === value.currency}
									getOptionLabel={(option) => (option ? `${option.currency} (${option.symbol})` : '')}
									options={CURRENCIES}
									fullWidth
									autoHighlight
									onChange={(e, value) => {
										formik.setFieldValue('currency', value?.currency);
									}}
									onInputChange={(_e, _value, reason) => {
										if (reason === 'clear') {
											formik.setFieldValue('currency', '');
										}
									}}
									inputValue={
										formik.values.currency || COUNTRIES.find(({ iso }) => iso === formik.values.country)?.currency
									}
									renderInput={(params) => (
										<FormikTextfield
											{...params}
											formikApi={formik}
											name="currency"
											title={t('product.currency')}
											placeholder={t('product.currency-placeholder')}
											required
											InputProps={{
												...params.InputProps,
												endAdornment: params.InputProps.endAdornment,
											}}
										/>
									)}
								/>
							</Grid>

							<Grid item xs={4}>
								<FormikNumber
									formikApi={formik}
									name="price"
									title={t('product.price')}
									placeholder={t('product.price-placeholder')}
									required
									decimal={2}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									select
									formikApi={formik}
									name="priceType"
									title={t('product.price-type')}
									placeholder={t('product.price-type-placeholder')}
									required
								>
									{Object.values(ProductPriceType).map((type) => (
										<MenuItem key={`product-price-type-${type}`} value={type}>
											{getProductPriceTypeText(type)}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									select
									formikApi={formik}
									name="priceRecurrence"
									title={t('product.price-recurrence-type')}
									placeholder={t('product.price-recurrence-type-placeholder')}
									required
								>
									{Object.values(ProductPriceRecurrence).map((type) => (
										<MenuItem key={`product-price-recurrency-type-${type}`} value={type}>
											{getProductPriceRecurrenceText(type)}
										</MenuItem>
									))}
								</FormikTextfield>
							</Grid>

							{formik.values.category === ProductCategoryType.CHARGING_POINT && (
								<Grid item xs={12}>
									<FormStepGreySection sx={{ marginTop: 0 }}>
										<Stack direction="column" spacing={6}>
											<FormStepQuestion
												title={<Tr.Portal path="product.add-edit-entity.charger-point-details" />}
												titleProps={{ variant: 'subtitle1' }}
											>
												<Grid container rowSpacing={5} columnSpacing={4}>
													<Grid item xs={8}>
														<FormikTextfield
															formikApi={formik}
															name="brand"
															title={t('product.brand')}
															placeholder={t('product.brand')}
															required
														/>
													</Grid>
													<Grid item xs={4}>
														<FormikNumber
															formikApi={formik}
															name="numberOfConnectors"
															title={t('product.number-of-connectors')}
															placeholder={t('product.number-of-connectors')}
															required
															decimal={0}
														/>
													</Grid>

													<Grid item xs={4}>
														<FormikNumber
															formikApi={formik}
															name="maximumPower"
															title={t('product.maximum-power')}
															placeholder={t('product.maximum-power')}
															required
															decimal={1}
														/>
													</Grid>
													<Grid item xs={4}>
														<FormikNumber
															formikApi={formik}
															name="ratedVoltage"
															title={t('product.rated-voltage')}
															placeholder={t('product.rated-voltage')}
															required
															decimal={1}
														/>
													</Grid>
													<Grid item xs={4}>
														<FormikNumber
															formikApi={formik}
															name="maxCurrent"
															title={t('product.max-current')}
															placeholder={t('product.max-current')}
															required
															decimal={1}
														/>
													</Grid>

													<Grid item xs={4}>
														<FormikTextfield
															select
															formikApi={formik}
															name="chargerUsingType"
															title={t('product.charger-using-type')}
															placeholder={t('product.charger-using-type')}
															required
														>
															{Object.values(ChargerUsingType).map((type) => (
																<MenuItem key={`chargerUsingType-${type}`} value={type}>
																	{getChargerUsingTypeText(type)}
																</MenuItem>
															))}
														</FormikTextfield>
													</Grid>
													<Grid item xs={4}>
														<FormikTextfield
															select
															formikApi={formik}
															name="chargerPhaseType"
															title={t('product.charger-phase-type')}
															placeholder={t('product.charger-phase-type')}
															required
														>
															{Object.values(ChargerPhaseType).map((type) => (
																<MenuItem key={`charger-phase-type-${type}`} value={type}>
																	{getChargerPhaseTypeText(type)}
																</MenuItem>
															))}
														</FormikTextfield>
													</Grid>
													<Grid item xs={4}>
														<FormikTextfield
															select
															formikApi={formik}
															name="currentType"
															title={t('product.current-type')}
															placeholder={t('product.current-type')}
															required
														>
															{Object.values(ChargerCurrentType).map((type) => (
																<MenuItem key={`currentType-${type}`} value={type}>
																	{type.toUpperCase()}
																</MenuItem>
															))}
														</FormikTextfield>
													</Grid>

													<Grid item xs={4}>
														<FormikRadioGroup title={t('product.cable-included')}>
															<Stack direction="row" spacing={2}>
																<FormikRadio
																	formikApi={formik}
																	name="cableIncluded"
																	value="1"
																	formControlLabelProps={{ label: <Tr.Portal path="yes" /> }}
																/>
																<FormikRadio
																	formikApi={formik}
																	name="cableIncluded"
																	value="0"
																	formControlLabelProps={{ label: <Tr.Portal path="no" /> }}
																/>
															</Stack>
														</FormikRadioGroup>
													</Grid>
													<Grid item xs={4}>
														<FormikRadioGroup title={t('product.pole-needed')}>
															<Stack direction="row" spacing={2}>
																<FormikRadio
																	formikApi={formik}
																	name="poleNeeded"
																	value="1"
																	formControlLabelProps={{ label: <Tr.Portal path="yes" /> }}
																/>
																<FormikRadio
																	formikApi={formik}
																	name="poleNeeded"
																	value="0"
																	formControlLabelProps={{ label: <Tr.Portal path="no" /> }}
																/>
															</Stack>
														</FormikRadioGroup>
													</Grid>

													<Grid item xs={4}>
														<ImageUpload
															title={<Tr.Portal path="product.product-image" />}
															images={currentImages}
															onChange={(files) => (setNewImages(files), setImagesChanged(true))}
														/>
													</Grid>

													<Grid item xs={4}>
														<FormikRadioGroup title={t('product.home-usage')}>
															<Stack direction="row" spacing={2}>
																<FormikRadio
																	formikApi={formik}
																	name="homeUsage"
																	value="1"
																	formControlLabelProps={{ label: <Tr.Portal path="yes" /> }}
																/>
																<FormikRadio
																	formikApi={formik}
																	name="homeUsage"
																	value="0"
																	formControlLabelProps={{ label: <Tr.Portal path="no" /> }}
																/>
															</Stack>
														</FormikRadioGroup>
													</Grid>

													<Grid item xs={4}>
														<FormikRadioGroup title={t('product.office-usage')}>
															<Stack direction="row" spacing={2}>
																<FormikRadio
																	formikApi={formik}
																	name="officeUsage"
																	value="1"
																	formControlLabelProps={{ label: <Tr.Portal path="yes" /> }}
																/>
																<FormikRadio
																	formikApi={formik}
																	name="officeUsage"
																	value="0"
																	formControlLabelProps={{ label: <Tr.Portal path="no" /> }}
																/>
															</Stack>
														</FormikRadioGroup>
													</Grid>
												</Grid>
											</FormStepQuestion>
											<FormStepQuestion
												title={<Tr.Portal path="product.accessory-types" />}
												titleProps={{ variant: 'body2', sx: { marginBottom: 1 } }}
											>
												<Grid container rowSpacing={0} columnSpacing={2}>
													{Object.values(AccessoryType).map((type) => (
														<Grid item xs={4} key={`requiredAccessoryTypes-${type}`}>
															<FormikCheckbox
																formikApi={formik}
																name="requiredAccessoryTypes"
																value={type}
																formControlLabelProps={{
																	label: getAccessoryTypeText(type),
																}}
															/>
														</Grid>
													))}
												</Grid>
											</FormStepQuestion>
										</Stack>
									</FormStepGreySection>
								</Grid>
							)}

							{formik.values.category === ProductCategoryType.ACCESSORIES && (
								<Grid item xs={12}>
									<FormStepGreySection sx={{ marginTop: 0 }}>
										<FormStepQuestion
											title={<Tr.Portal path="product.add-edit-entity.accessory-details" />}
											titleProps={{ variant: 'subtitle1' }}
										>
											<Grid container rowSpacing={5} columnSpacing={4}>
												<Grid item xs={4}>
													<FormikTextfield
														select
														formikApi={formik}
														name="accessoryType"
														title={t('product.accessory-type')}
														placeholder={t('product.accessory-type')}
														required
													>
														{Object.values(AccessoryType).map((type) => (
															<MenuItem key={`accessoryTypee-${type}`} value={type}>
																{getAccessoryTypeText(type)}
															</MenuItem>
														))}
													</FormikTextfield>
												</Grid>
												<Grid item xs={4}>
													<FormikTextfield
														select
														formikApi={formik}
														name="chargerPhaseType"
														title={t('product.charger-phase-type')}
														placeholder={t('product.charger-phase-type')}
														required
													>
														{Object.values(ChargerPhaseType).map((type) => (
															<MenuItem key={`charger-phase-type-${type}`} value={type}>
																{getChargerPhaseTypeText(type)}
															</MenuItem>
														))}
													</FormikTextfield>
												</Grid>
												<Grid item xs={4}>
													<FormikNumber
														formikApi={formik}
														name="numberOfAccesoryPerChargingPoint"
														title={t('product.number-of-accesory-per-charging-point')}
														placeholder={t('product.number-of-accesory-per-charging-point')}
														required
														decimal={2}
													/>
												</Grid>
											</Grid>
										</FormStepQuestion>
									</FormStepGreySection>
								</Grid>
							)}

							{formik.values.category === ProductCategoryType.SERVICE && (
								<Grid item xs={12}>
									<FormStepGreySection sx={{ marginTop: 0 }}>
										<FormStepQuestion
											title={<Tr.Portal path="product.add-edit-entity.service-details" />}
											titleProps={{ variant: 'subtitle1' }}
										>
											<Grid container rowSpacing={5} columnSpacing={4}>
												<Grid item xs={4}>
													<FormikTextfield
														select
														formikApi={formik}
														name="serviceType"
														title={t('product.service-type')}
														placeholder={t('product.service-type')}
														required
													>
														{Object.values(ServiceType).map((type) => (
															<MenuItem key={`serviceType-${type}`} value={type}>
																{getServiceTypeText(type)}
															</MenuItem>
														))}
													</FormikTextfield>
												</Grid>
											</Grid>
										</FormStepQuestion>
									</FormStepGreySection>
								</Grid>
							)}
						</Grid>
					</FormStepQuestion>
				</Stack>
			</FormStepLayout>
		</form>
	);
};
